import React, { Component, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import i18n from '../../i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary — catches unhandled render errors in any child component tree.
 * Without this, a single crashed component tears down the entire React app.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * Or with a custom fallback:
 *   <ErrorBoundary fallback={<p>Something went wrong</p>}>
 *     <SomeComponent />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Errors are still reported via console.error (kept in production)
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minHeight: '200px', p: 4, textAlign: 'center',
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            {i18n.t('ui:somethingWentWrong')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, maxWidth: 400 }}>
            {i18n.t('ui:anUnexpectedErrorOccurredInThis')}
          </Typography>
          <Button variant="outlined" onClick={this.handleReset}>
            {i18n.t('ui:tryAgain')}
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
